import dayjs from 'dayjs';
import { Dispatch, SetStateAction, useMemo } from 'react';

import { Cell } from './Cell';
import { Controls } from './Controls';
import { t } from '../../services/translation/translation';

type Props = {
  year: number;
  month: number;
  onSelectDay: (_timestamp: number) => void;
  selectedTimestamp?: number;
  onHeaderClick: () => void;
  setYear: Dispatch<SetStateAction<number>>;
  setMonth: Dispatch<SetStateAction<number>>;
};

export const DaysView = ({ year, month, selectedTimestamp, onSelectDay, onHeaderClick, setMonth, setYear }: Props) => {
  const { daysBefore, days, daysAfter } = useMemo(() => {
    const selectedMonth = dayjs({ year, month });
    const start = selectedMonth.startOf('month');
    const end = selectedMonth.endOf('month');
    const nbDays = end.get('date');
    const nbDaysBefore = start.weekday();
    const nbDaysAfter = 42 - nbDaysBefore - nbDays;

    return {
      daysBefore: Array.from({ length: nbDaysBefore })
        .map((_, index) => {
          const day = selectedMonth.subtract(1, 'month').endOf('month').subtract(index, 'day').startOf('day');
          return { label: day.get('D'), date: day.valueOf() };
        })
        .reverse(),
      days: Array.from({ length: nbDays }).map((_, index) => ({
        label: index + 1,
        date: start.add(index, 'day').startOf('day').valueOf(),
      })),
      daysAfter: Array.from({ length: nbDaysAfter }).map((_, index) => ({
        label: index + 1,
        date: selectedMonth.add(1, 'month').startOf('month').add(index, 'day').startOf('day').valueOf(),
      })),
    };
  }, [month, year]);

  const handlePrevious = () => {
    const previousMonth = dayjs({ year, month }).subtract(1, 'month');

    setYear(previousMonth.get('year'));
    setMonth(previousMonth.get('month'));
  };

  const handleNext = () => {
    const previousMonth = dayjs({ year, month }).add(1, 'month');

    setYear(previousMonth.get('year'));
    setMonth(previousMonth.get('month'));
  };

  return (
    <div>
      <Controls
        onHeaderClick={onHeaderClick}
        onPrevious={handlePrevious}
        onNext={handleNext}
        label={dayjs({ year, month }).format(t('date-picker.days'))}
      />

      <div>
        <div className="grid grid-cols-7 mb-1">
          {Array.from({ length: 7 }).map((_, index) => (
            <span
              key={index}
              className="text-center h-6 leading-6 text-sm font-medium capitalize text-gray-500 dark:text-gray-400"
            >
              {dayjs().weekday(index).format('dd')}
            </span>
          ))}
        </div>
        <div className="w-64 grid grid-cols-7">
          {daysBefore.map((day, index) => (
            <Cell key={index} onClick={() => onSelectDay(day.date)} isSecondary>
              {day.label}
            </Cell>
          ))}
          {days.map((day, index) => (
            <Cell key={index} isSelected={selectedTimestamp === day.date} onClick={() => onSelectDay(day.date)}>
              {day.label}
            </Cell>
          ))}
          {daysAfter.map((day, index) => (
            <Cell key={index} onClick={() => onSelectDay(day.date)} isSecondary>
              {day.label}
            </Cell>
          ))}
        </div>
      </div>
    </div>
  );
};
